<div class="easypass-dialog-container flex flex-col h-full max-h-[90vh]">
    <!-- Dialog Header -->
    <div class="flex items-start justify-between gap-4 p-5 sm:p-6 border-b border-slate-100 bg-slate-50/70 shrink-0">
        <div class="flex items-start gap-3">
            <div class="w-10 h-10 rounded-xl bg-blue-600/10 text-blue-600 flex items-center justify-center shrink-0 ring-1 ring-blue-600/20">
                <mat-icon class="!w-6 !h-6 !text-[24px] leading-6">qr_code_2</mat-icon>
            </div>
            <div>
                <div class="flex items-center gap-2 flex-wrap">
                    <h2 class="text-lg font-bold text-slate-900 leading-tight">
                        {{ issued() ? 'Easypass Invitation Ready' : 'Create Easypass Invitation' }}
                    </h2>
                    @if (parkingSpace.name) {
                    <span class="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-slate-200 text-slate-700">
                        <mat-icon class="!w-3.5 !h-3.5 !text-[13px] leading-3.5 text-slate-500">local_parking</mat-icon>
                        {{ parkingSpace.name }}
                    </span>
                    }
                </div>
                <p class="text-xs sm:text-sm text-slate-500 mt-1">
                    {{ issued()
                        ? 'Share this QR code or link with attendees. Guests register their license plate directly.'
                        : 'Generate a shareable QR code & redemption link for visitors to register their vehicle.' }}
                </p>
            </div>
        </div>
        <button type="button" (click)="close()"
            class="text-slate-400 hover:text-slate-600 hover:bg-slate-200/50 p-1.5 rounded-lg transition-colors shrink-0"
            aria-label="Close dialog">
            <mat-icon class="!w-5 !h-5 !text-[20px] leading-5">close</mat-icon>
        </button>
    </div>

    <!-- Dialog Content Scroll Area -->
    <div class="overflow-y-auto px-5 py-5 sm:px-6 space-y-6 flex-1">

        <!-- ================= SUCCESS STATE: ISSUED INVITATION ================= -->
        @if (issued(); as pass) {
        <div class="space-y-6 animate-fadeIn">
            <!-- Success Notification Banner -->
            <div class="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                <div class="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center shrink-0 shadow-xs">
                    <mat-icon class="!w-5 !h-5 !text-[20px] leading-5">check</mat-icon>
                </div>
                <div class="flex-1 min-w-0">
                    <div class="flex items-center gap-2 flex-wrap">
                        <h3 class="text-sm font-bold text-emerald-900">
                            @if (mode() === 'simple') {
                            {{ 'easypass.activeFor' | translate:{ plate: issuedPlate() } }}
                            } @else {
                            "{{ pass.title || 'Easypass Invitation' }}" is active
                            }
                        </h3>
                        <span class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-800">
                            Live & Ready
                        </span>
                    </div>
                    @if (mode() === 'advanced') {
                    <p class="text-xs text-emerald-700 mt-0.5">
                        Share this link or QR with guests. Each vehicle redeems it once with their license plate.
                    </p>
                    }
                </div>
            </div>

            <!-- Desk issue: the pass is already redeemed for this plate, so there is no QR to hand
                 over and no redemption link that would do anything. Confirm the vehicle instead. -->
            @if (mode() === 'simple') {
            <div class="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-4">
                <div class="flex items-center gap-3">
                    <mat-icon class="!w-5 !h-5 !text-[20px] leading-5 text-blue-600">directions_car</mat-icon>
                    <span class="text-lg font-bold tracking-wider text-slate-900 font-mono">{{ issuedPlate() }}</span>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div class="bg-slate-50 p-3 rounded-xl border border-slate-200/60">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">{{ 'easypass.validFrom' | translate }}</span>
                        <div class="mt-1 font-medium text-slate-800 text-xs sm:text-sm">
                            {{ pass.validFrom ? (pass.validFrom | date:'dd.MM.yyyy HH:mm') : '—' }}
                        </div>
                    </div>

                    <div class="bg-slate-50 p-3 rounded-xl border border-slate-200/60">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">{{ 'easypass.validTo' | translate }}</span>
                        <div class="mt-1 font-medium text-slate-800 text-xs sm:text-sm">
                            {{ pass.validTo ? (pass.validTo | date:'dd.MM.yyyy HH:mm') : '—' }}
                        </div>
                    </div>

                    <div class="bg-slate-50 p-3 rounded-xl border border-slate-200/60">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">{{ 'easypass.billedTo' | translate }}</span>
                        <div class="mt-1 font-medium text-slate-800 text-xs sm:text-sm">
                            {{ issuedOrganizationName() || ('easypass.noOrganization' | translate) }}
                        </div>
                    </div>
                </div>
            </div>
            } @else if (mode() === 'exitTickets') {
            <!-- Exit tickets: the shared link is refused for these, so there is nothing to share —
                 only the printed codes. -->
            <div class="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-4">
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div class="bg-slate-50 p-3 rounded-xl border border-slate-200/60">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">{{ 'easypass.title' | translate }}</span>
                        <div class="mt-1 font-medium text-slate-800 text-xs sm:text-sm truncate">{{ pass.title }}</div>
                    </div>
                    <div class="bg-slate-50 p-3 rounded-xl border border-slate-200/60">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">{{ 'easypass.validUntil' | translate }}</span>
                        <div class="mt-1 font-medium text-slate-800 text-xs sm:text-sm">{{ pass.validTo | date:'dd.MM.yyyy' }}</div>
                    </div>
                    <div class="bg-slate-50 p-3 rounded-xl border border-slate-200/60">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">{{ 'easypass.codeCount' | translate }}</span>
                        <div class="mt-1 font-medium text-slate-800 text-xs sm:text-sm">{{ pass.codeCount }}</div>
                    </div>
                </div>
                <button type="button" (click)="openPrint(pass)"
                    class="inline-flex items-center gap-2 px-4 py-2.5 text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-xs transition-colors">
                    <mat-icon class="!w-4 !h-4 !text-[18px] leading-4">print</mat-icon>
                    <span>{{ 'easypass.printCodes' | translate }}</span>
                </button>
            </div>
            } @else {

            <!-- QR Code Card & Sharing Hub -->
            <div class="p-5 rounded-2xl bg-white border border-slate-200 shadow-xs space-y-5">
                <div class="flex flex-col sm:flex-row gap-6 items-center sm:items-start">
                    <!-- QR Box -->
                    <div class="shrink-0 p-3 bg-white rounded-xl ring-1 ring-slate-200 shadow-xs flex flex-col items-center">
                        @if (redeemLink) {
                        <qrcode [qrdata]="redeemLink" [width]="200" [margin]="2" [colorDark]="'#0f172a'"
                            [colorLight]="'#FFFFFF'" elementType="canvas" cssClass="rounded-lg block"></qrcode>
                        }
                        <span class="text-[11px] font-medium text-slate-400 mt-2">Scan with smartphone</span>
                    </div>

                    <!-- Link & Action Buttons -->
                    <div class="flex-1 w-full space-y-4">
                        <div>
                            <label class="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5">
                                Public Redemption Link
                            </label>
                            <div class="flex items-center gap-2">
                                <input type="text" readonly [value]="redeemLink"
                                    class="w-full px-3 py-2 text-xs sm:text-sm bg-slate-50 rounded-xl border border-slate-300 text-slate-700 font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
                                <button type="button" (click)="copyLink()"
                                    matTooltip="Copy redeem link"
                                    class="px-3 py-2 text-xs sm:text-sm font-semibold rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors shrink-0 flex items-center gap-1.5 shadow-xs">
                                    <mat-icon class="!w-4 !h-4 !text-[16px] leading-4">content_copy</mat-icon>
                                </button>
                            </div>
                        </div>

                        <!-- Action Buttons Row -->
                        <div class="flex flex-wrap gap-2.5">
                            <button type="button" (click)="copyLink()"
                                class="inline-flex items-center gap-2 px-4 py-2.5 text-xs sm:text-sm font-semibold rounded-xl transition-all shadow-xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                                [class]="copied() ? 'bg-emerald-600 text-white hover:bg-emerald-700' : 'bg-blue-600 hover:bg-blue-700 text-white'">
                                <mat-icon class="!w-4 !h-4 !text-[18px] leading-4">{{ copied() ? 'check' : 'share' }}</mat-icon>
                                <span>{{ copied() ? 'Link Copied!' : 'Copy Invitation Link' }}</span>
                            </button>

                            <a [href]="redeemLink" target="_blank" rel="noopener"
                                class="inline-flex items-center gap-2 px-3.5 py-2.5 text-xs sm:text-sm font-semibold rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors shadow-xs"
                                matTooltip="Open the redemption page in the parker app">
                                <mat-icon class="!w-4 !h-4 !text-[18px] leading-4 text-slate-600">open_in_new</mat-icon>
                                <span>Redeem</span>
                            </a>

                            <button type="button" (click)="downloadQrPng()"
                                class="inline-flex items-center gap-2 px-3.5 py-2.5 text-xs sm:text-sm font-semibold rounded-xl border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 transition-colors shadow-xs">
                                <mat-icon class="!w-4 !h-4 !text-[18px] leading-4 text-slate-600">download</mat-icon>
                                <span>Download QR (PNG)</span>
                            </button>

                        </div>

                        @if (downloadError()) {
                        <p class="text-xs text-red-600 flex items-center gap-1">
                            <mat-icon class="!w-4 !h-4 !text-[14px] leading-4">warning</mat-icon>
                            {{ downloadError() }}
                        </p>
                        }

                        <p class="text-xs text-slate-500 leading-relaxed">
                            Guests open this URL, enter their license plate, and receive instant gate access during the specified timeframe.
                        </p>
                    </div>
                </div>
            </div>
            }

            <!-- Parameters Breakdown Grid — the shared invitation's settings. A desk pass has none
                 worth restating: the card above already says which car, until when, and who pays. -->
            @if (mode() === 'advanced') {
            <div class="p-4 sm:p-5 rounded-2xl bg-slate-50 border border-slate-200/80 space-y-3">
                <h4 class="text-xs font-bold uppercase tracking-wider text-slate-500">Invitation Specifications</h4>
                <div class="grid grid-cols-2 sm:grid-cols-3 gap-4 pt-1">
                    <div class="bg-white p-3 rounded-xl border border-slate-200/60 shadow-2xs">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">Vehicle Limit</span>
                        <div class="flex items-center gap-1.5 mt-1 font-semibold text-slate-900 text-sm">
                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-blue-600">directions_car</mat-icon>
                            {{ capacityDisplay(pass) }}
                        </div>
                    </div>

                    <div class="bg-white p-3 rounded-xl border border-slate-200/60 shadow-2xs">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">Allowed Entries</span>
                        <div class="flex items-center gap-1.5 mt-1 font-semibold text-slate-900 text-sm">
                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-purple-600">repeat</mat-icon>
                            {{ entriesDisplay(pass) }}
                        </div>
                    </div>

                    <div class="bg-white p-3 rounded-xl border border-slate-200/60 shadow-2xs">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">Payment Policy</span>
                        <div class="flex items-center gap-1.5 mt-1 font-semibold text-slate-900 text-sm">
                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-emerald-600">credit_card</mat-icon>
                            {{ pass.paymentBehavior === PaymentBehavior.ParkerPays ? 'Visitor Pays Tariff' : 'Free for Visitor' }}
                        </div>
                    </div>

                    <div class="bg-white p-3 rounded-xl border border-slate-200/60 shadow-2xs">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">Valid Window</span>
                        <div class="flex items-center gap-1.5 mt-1 font-medium text-slate-800 text-xs sm:text-sm">
                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-amber-500">event</mat-icon>
                            <span class="truncate">
                                {{ pass.validFrom ? (pass.validFrom | date:'dd.MM.yyyy HH:mm') : 'Immediate' }}
                                →
                                {{ pass.validTo ? (pass.validTo | date:'dd.MM.yyyy HH:mm') : 'No expiry' }}
                            </span>
                        </div>
                    </div>

                    <div class="bg-white p-3 rounded-xl border border-slate-200/60 shadow-2xs">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">Zone Scope</span>
                        <div class="flex items-center gap-1.5 mt-1 font-medium text-slate-800 text-xs sm:text-sm">
                            <mat-icon class="!w-4 !h-4 !text-[16px] leading-4 text-indigo-500">domain</mat-icon>
                            <span class="truncate">{{ zoneScopeDisplay(pass) }}</span>
                        </div>
                    </div>

                    <div class="bg-white p-3 rounded-xl border border-slate-200/60 shadow-2xs">
                        <span class="text-[11px] font-semibold text-slate-400 uppercase tracking-wider block">Token Code</span>
                        <div class="flex items-center justify-between gap-1 mt-1 font-mono font-semibold text-slate-900 text-xs sm:text-sm">
                            <span class="truncate">{{ pass.token }}</span>
                            <button type="button" (click)="copyToken(pass.token)" matTooltip="Copy token"
                                class="text-slate-400 hover:text-slate-700 transition-colors p-0.5 rounded">
                                <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-3.5">
                                    {{ tokenCopied() ? 'check' : 'content_copy' }}
                                </mat-icon>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
            }
        </div>
        } @else {

        <!-- ================= CREATE FORM ================= -->
        <form [formGroup]="form" class="space-y-6">

            <!-- Mode toggle: only the modes switched on for this space; none to pick when it is one. -->
            @if (modes.length > 1) {
            <div class="flex items-center justify-between gap-3 p-1 rounded-xl bg-slate-100">
                <div class="flex-1 grid gap-1" [style.grid-template-columns]="'repeat(' + modes.length + ', minmax(0, 1fr))'">
                    @for (m of modes; track m) {
                    <button type="button" (click)="setMode(m)"
                        class="px-3 py-2 rounded-lg text-xs sm:text-sm font-semibold transition-all"
                        [class]="mode() === m ? 'bg-white text-blue-600 shadow-2xs' : 'text-slate-500 hover:text-slate-700'">
                        {{ (m === 'simple' ? 'easypass.modeSimple' : m === 'exitTickets' ? 'easypass.modeExitTickets' : 'easypass.modeAdvanced') | translate }}
                    </button>
                    }
                </div>
            </div>
            }

            @if (mode() === 'exitTickets') {
            <div class="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-4">
                <div>
                    <label class="block text-xs font-semibold text-slate-700 mb-1.5">
                        {{ 'easypass.title' | translate }} <span class="text-red-500">*</span>
                    </label>
                    <input type="text" formControlName="title" maxlength="200"
                        class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-xs font-semibold text-slate-700 mb-1.5">
                            {{ 'easypass.validUntil' | translate }} <span class="text-red-500">*</span>
                        </label>
                        <input type="date" formControlName="validUntil"
                            class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>

                    <div>
                        <label class="block text-xs font-semibold text-slate-700 mb-1.5">
                            {{ 'easypass.codeCount' | translate }} <span class="text-red-500">*</span>
                        </label>
                        <input type="number" formControlName="codeCount" min="1" [max]="maxExitTickets" step="1"
                            class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                        @if (form.get('codeCount')?.touched && (form.get('codeCount')?.hasError('min') || form.get('codeCount')?.hasError('max') || form.get('codeCount')?.hasError('pattern'))) {
                        <p class="mt-1 text-xs text-red-600">{{ 'easypass.codeCountRange' | translate: { max: maxExitTickets } }}</p>
                        }
                    </div>
                </div>

                <div>
                    <label class="block text-xs font-semibold text-slate-700 mb-1.5">{{ 'easypass.note' | translate }}</label>
                    <textarea formControlName="comment" rows="2"
                        class="w-full px-3.5 py-2 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"></textarea>
                </div>
            </div>
            }

            @if (mode() === 'simple') {
            <div class="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-4">
                <div>
                    <label class="block text-xs font-semibold text-slate-700 mb-1.5">
                        {{ 'easypass.licensePlate' | translate }} <span class="text-red-500">*</span>
                    </label>
                    <input type="text" formControlName="licensePlate" (blur)="onLicensePlateBlur()"
                        (input)="onLicensePlateInput($event)" [maxlength]="maxPlateLength"
                        autocomplete="off" spellcheck="false"
                        class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all uppercase" />
                    <!-- A car already inside is billed short-term by the minute; the start is pulled
                         back to its entry so those minutes are covered. -->
                    @if (plateLookupLoading()) {
                    <mat-spinner diameter="14" class="!inline-block mt-1.5"></mat-spinner>
                    } @else if (activeStay()) {
                    <p class="text-[11px] text-amber-700 mt-1">
                        {{ 'easypass.parkedSince' | translate:{ time: activeStaySince() } }}
                    </p>
                    }
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-xs font-semibold text-slate-700 mb-1.5">{{ 'easypass.validFrom' | translate }}</label>
                        <input type="datetime-local" formControlName="validFrom"
                            class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>

                    <div>
                        <label class="block text-xs font-semibold text-slate-700 mb-1.5">{{ 'easypass.validTo' | translate }}</label>
                        <input type="datetime-local" formControlName="validTo"
                            class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    </div>
                </div>

                <div>
                    <label class="block text-xs font-semibold text-slate-700 mb-1.5">{{ 'easypass.organization' | translate }}</label>
                    <jjames-autocomplete-create
                        [options]="organizationOptions()"
                        [value]="form.get('organizationId')?.value"
                        [loading]="orgsLoading()"
                        [disabled]="orgsLoading()"
                        [allowCreate]="false"
                        [placeholder]="'easypass.organizationPlaceholder' | translate"
                        (valueChange)="onOrganizationSelected($event)">
                    </jjames-autocomplete-create>
                </div>

                <div>
                    <label class="block text-xs font-semibold text-slate-700 mb-1.5">{{ 'easypass.costCenter' | translate }}</label>
                    <jjames-autocomplete-create
                        [options]="costCenterOptions()"
                        [value]="form.get('costCenterId')?.value"
                        [loading]="costCenterLoading()"
                        [disabled]="!form.get('organizationId')?.value"
                        [placeholder]="'easypass.costCenterPlaceholder' | translate"
                        (valueChange)="onCostCenterSelected($event)"
                        (createNew)="onCostCenterCreateNew($event)">
                    </jjames-autocomplete-create>
                </div>

                <div>
                    <label class="block text-xs font-semibold text-slate-700 mb-1.5">{{ 'easypass.note' | translate }}</label>
                    <textarea formControlName="comment" rows="2"
                        class="w-full px-3.5 py-2 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"></textarea>
                </div>
            </div>
            }

            @if (mode() === 'advanced') {

            <!-- Section 1: Invitation & Event Details -->
            <div class="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-4">
                <div class="flex items-center gap-2">
                    <mat-icon class="!w-4 !h-4 !text-[18px] leading-4 text-blue-600">event_note</mat-icon>
                    <h3 class="text-xs font-bold uppercase tracking-wider text-slate-600">Event & Title</h3>
                </div>

                <div>
                    <label class="block text-xs font-semibold text-slate-700 mb-1.5">
                        Invitation Title <span class="text-red-500">*</span>
                    </label>
                    <input type="text" formControlName="title" placeholder="e.g. Annual Tech Symposium, VIP Board Visit, Supplier Access"
                        class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                    @if (form.get('title')?.touched && form.get('title')?.invalid) {
                    <p class="text-xs text-red-600 mt-1 flex items-center gap-1">
                        <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-3.5">error_outline</mat-icon>
                        Please provide a title for this invitation.
                    </p>
                    }
                </div>

                <div>
                    <label class="block text-xs font-semibold text-slate-700 mb-1.5">
                        Public Description / Guest Note <span class="text-slate-400 font-normal">(Optional)</span>
                    </label>
                    <textarea formControlName="description" rows="2"
                        placeholder="e.g. Free parking for keynote guests. Please park in Zone B."
                        class="w-full px-3.5 py-2 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"></textarea>
                    <p class="text-[11px] text-slate-400 mt-1">Displayed to guests on their phone when they open the redemption link.</p>
                </div>
            </div>

            <!-- Section 2: Validity Window -->
            <div class="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-4">
                <div class="flex items-center gap-2">
                    <mat-icon class="!w-4 !h-4 !text-[18px] leading-4 text-blue-600">calendar_month</mat-icon>
                    <h3 class="text-xs font-bold uppercase tracking-wider text-slate-600">Validity Period</h3>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label class="block text-xs font-semibold text-slate-700 mb-1.5">Valid From</label>
                        <input type="datetime-local" formControlName="validFrom"
                            class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                        <p class="text-[11px] text-slate-400 mt-1">Date <em>and time</em>, in your local time. Immediate if left blank</p>
                    </div>

                    <div>
                        <label class="block text-xs font-semibold text-slate-700 mb-1.5">Valid To</label>
                        <input type="datetime-local" formControlName="validTo"
                            class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                        <p class="text-[11px] text-slate-400 mt-1">Date <em>and time</em>, in your local time. No expiry if left blank</p>
                    </div>
                </div>
            </div>

            <!-- Section 3: Vehicle Limit & Re-entries -->
            <div class="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-5">
                <div class="flex items-center gap-2">
                    <mat-icon class="!w-4 !h-4 !text-[18px] leading-4 text-blue-600">directions_car</mat-icon>
                    <h3 class="text-xs font-bold uppercase tracking-wider text-slate-600">Capacity & Re-entry Policy</h3>
                </div>

                <!-- Capacity Type Selector (Cards) -->
                <div>
                    <label class="block text-xs font-semibold text-slate-700 mb-2">Vehicle Redemption Limit</label>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button type="button" (click)="setCapacityMode(false)"
                            class="p-3.5 rounded-xl border text-left transition-all relative flex items-start gap-3 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                            [class]="!isUnlimitedCapacity()
                                ? 'border-blue-600 bg-blue-50/70 ring-1 ring-blue-600 text-slate-900'
                                : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'">
                            <div class="w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-0.5"
                                [class]="!isUnlimitedCapacity() ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white'">
                                @if (!isUnlimitedCapacity()) {
                                <div class="w-2 h-2 rounded-full bg-white"></div>
                                }
                            </div>
                            <div>
                                <span class="block text-sm font-semibold text-slate-900">Fixed Vehicle Limit</span>
                                <span class="block text-xs text-slate-500 mt-0.5">Cap the total number of license plates allowed.</span>
                            </div>
                        </button>

                        <button type="button" (click)="setCapacityMode(true)"
                            class="p-3.5 rounded-xl border text-left transition-all relative flex items-start gap-3 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                            [class]="isUnlimitedCapacity()
                                ? 'border-blue-600 bg-blue-50/70 ring-1 ring-blue-600 text-slate-900'
                                : 'border-slate-200 bg-white hover:border-slate-300 text-slate-700'">
                            <div class="w-5 h-5 rounded-full border flex items-center justify-center shrink-0 mt-0.5"
                                [class]="isUnlimitedCapacity() ? 'border-blue-600 bg-blue-600 text-white' : 'border-slate-300 bg-white'">
                                @if (isUnlimitedCapacity()) {
                                <div class="w-2 h-2 rounded-full bg-white"></div>
                                }
                            </div>
                            <div>
                                <span class="block text-sm font-semibold text-slate-900">Unlimited Vehicles</span>
                                <span class="block text-xs text-slate-500 mt-0.5">Open to any number of guests until revoked.</span>
                            </div>
                        </button>
                    </div>
                </div>

                <!-- If Fixed Limit: Inputs for count & increase deadline -->
                @if (!isUnlimitedCapacity()) {
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1 animate-fadeIn">
                    <div>
                        <label class="block text-xs font-semibold text-slate-700 mb-1.5">Max License Plates</label>
                        <input type="number" min="1" formControlName="maxRedemptions" placeholder="1"
                            class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                        <p class="text-[11px] text-slate-400 mt-1">Maximum unique vehicles that can redeem this link</p>
                    </div>

                    <div>
                        <label class="block text-xs font-semibold text-slate-700 mb-1.5">
                            Raise Capacity Until <span class="text-slate-400 font-normal">(Optional)</span>
                        </label>
                        <input type="datetime-local" formControlName="increaseUntil"
                            class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                        <p class="text-[11px] text-slate-400 mt-1">Deadline to increase the plate limit later</p>
                    </div>
                </div>
                }

                <!-- Re-entries per Vehicle -->
                <div class="pt-2 border-t border-slate-100">
                    <label class="block text-xs font-semibold text-slate-700 mb-1.5">Re-entries per Vehicle</label>
                    <div class="relative">
                        <select formControlName="allowedEntries"
                            class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all appearance-none cursor-pointer">
                            @for (opt of entryOptions; track opt.value) {
                            <option [value]="opt.value">{{ opt.label }} — {{ opt.hint }}</option>
                            }
                        </select>
                        <div class="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-400">
                            <mat-icon class="!w-5 !h-5 !text-[20px] leading-5">expand_more</mat-icon>
                        </div>
                    </div>
                    <p class="text-[11px] text-slate-400 mt-1">Controls how many entries/exits each registered car may perform</p>
                </div>
            </div>

            <!-- Section 4: Payment & Billing Model -->
            <div class="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-4">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <mat-icon class="!w-4 !h-4 !text-[18px] leading-4 text-blue-600">payments</mat-icon>
                        <h3 class="text-xs font-bold uppercase tracking-wider text-slate-600">Billing & Payment</h3>
                    </div>
                    <span class="text-xs font-medium text-slate-500">Who settles the parking stay?</span>
                </div>

                <!-- Only organizations with access to this space or a zone of it are listed: an event billed to a
                     tenant with no parking here would reach someone unable to recognise it. -->
                <div>
                    <label class="block text-xs font-semibold text-slate-700 mb-1.5">
                        Issuing Organization <span class="text-slate-400 font-normal">(Optional)</span>
                    </label>
                    <jjames-autocomplete-create
                        [options]="organizationOptions()"
                        [value]="form.get('organizationId')?.value"
                        [loading]="orgsLoading()"
                        [disabled]="orgsLoading()"
                        [allowCreate]="false"
                        placeholder="Search organization…"
                        (valueChange)="onOrganizationSelected($event)">
                    </jjames-autocomplete-create>
                    @if (orgsLoading()) {
                    <p class="text-[11px] text-slate-400 mt-1">Loading organizations for this parking space…</p>
                    } @else if (organizations().length === 0) {
                    <p class="text-[11px] text-slate-400 mt-1">
                        No organization has access to this parking space, so this can only be the operator's own event.
                    </p>
                    } @else {
                    <p class="text-[11px] text-slate-400 mt-1">
                        The organization the event belongs to, and the one billed when guests park free.
                    </p>
                    }
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <!-- Option 1: Free for visitor / Org Pays -->
                    <button type="button" (click)="setPaymentBehavior(PaymentBehavior.OrgPays)"
                        class="p-4 rounded-xl border text-left transition-all relative flex flex-col justify-between shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        [class]="form.get('paymentBehavior')?.value === PaymentBehavior.OrgPays
                            ? 'border-blue-600 bg-blue-50/70 ring-1 ring-blue-600'
                            : 'border-slate-200 bg-white hover:border-slate-300'">
                        <div class="flex items-start justify-between gap-2">
                            <div class="flex items-center gap-2">
                                <div class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                    [class]="form.get('paymentBehavior')?.value === PaymentBehavior.OrgPays ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'">
                                    <mat-icon class="!w-4 !h-4 !text-[18px] leading-4">corporate_fare</mat-icon>
                                </div>
                                <span class="text-sm font-bold text-slate-900">Free for the Visitor</span>
                            </div>
                            <span class="px-2 py-0.5 rounded text-[11px] font-semibold bg-blue-100 text-blue-800">Org Billed</span>
                        </div>
                        <p class="text-xs text-slate-600 mt-2.5 leading-relaxed">
                            Guests exit free at the gate. The stays are tracked and billed to the issuing organization or operator.
                        </p>
                    </button>

                    <!-- Option 2: Parker Pays Tariff -->
                    <button type="button" (click)="setPaymentBehavior(PaymentBehavior.ParkerPays)"
                        class="p-4 rounded-xl border text-left transition-all relative flex flex-col justify-between shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        [class]="form.get('paymentBehavior')?.value === PaymentBehavior.ParkerPays
                            ? 'border-blue-600 bg-blue-50/70 ring-1 ring-blue-600'
                            : 'border-slate-200 bg-white hover:border-slate-300'">
                        <div class="flex items-start justify-between gap-2">
                            <div class="flex items-center gap-2">
                                <div class="w-8 h-8 rounded-lg flex items-center justify-center shrink-0"
                                    [class]="form.get('paymentBehavior')?.value === PaymentBehavior.ParkerPays ? 'bg-blue-600 text-white' : 'bg-slate-100 text-slate-600'">
                                    <mat-icon class="!w-4 !h-4 !text-[18px] leading-4">point_of_sale</mat-icon>
                                </div>
                                <span class="text-sm font-bold text-slate-900">Visitor Pays</span>
                            </div>
                            <span class="px-2 py-0.5 rounded text-[11px] font-semibold bg-emerald-100 text-emerald-800">Tariff Rate</span>
                        </div>
                        <p class="text-xs text-slate-600 mt-2.5 leading-relaxed">
                            Visitors settle the Easypass rate for this parking space on departure, via app or pay station.
                        </p>
                    </button>
                </div>

                <!-- The rate is not part of this form and is not created by issuing. Said plainly,
                     because "the visitor pays" is otherwise read as a charge that is guaranteed to
                     exist — and where no rate covers the space, the visitor simply exits free. -->
                <p class="flex items-start gap-2 text-xs text-slate-500 mt-3">
                    <mat-icon class="!w-4 !h-4 !text-[14px] leading-4 shrink-0 mt-px">info</mat-icon>
                    <span>
                        Issuing an invitation does not create a rate. Visitors are charged the Easypass
                        rate an operator has set for this parking space — where none exists, they exit
                        free. A single event can be priced separately in tariff management.
                    </span>
                </p>
            </div>

            <!-- Section 5: Zone Scope (if zones exist) -->
            @if (availableZones().length > 0) {
            <div class="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-3">
                <div class="flex items-center justify-between">
                    <div class="flex items-center gap-2">
                        <mat-icon class="!w-4 !h-4 !text-[18px] leading-4 text-blue-600">domain</mat-icon>
                        <h3 class="text-xs font-bold uppercase tracking-wider text-slate-600">Parking Zones Scope</h3>
                    </div>
                    <span class="text-xs text-slate-400">Optional</span>
                </div>

                <p class="text-xs text-slate-500">
                    Select specific zones to restrict entry. If none are selected, visitors can park anywhere in this space.
                </p>

                <div class="flex flex-wrap gap-2 pt-1">
                    <button type="button" (click)="selectAllZones()"
                        class="px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all flex items-center gap-1.5"
                        [class]="(form.get('selectedZones')?.value || []).length === 0
                            ? 'border-blue-600 bg-blue-600 text-white shadow-2xs'
                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'">
                        <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-3.5">
                            {{ (form.get('selectedZones')?.value || []).length === 0 ? 'check' : 'apps' }}
                        </mat-icon>
                        All Zones in Space
                    </button>

                    @for (zone of availableZones(); track zone.id) {
                    <button type="button" (click)="toggleZone(zone.id)"
                        class="px-3 py-1.5 rounded-xl text-xs font-semibold border transition-all flex items-center gap-1.5"
                        [class]="isZoneSelected(zone.id)
                            ? 'border-blue-600 bg-blue-600 text-white shadow-2xs'
                            : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'">
                        @if (isZoneSelected(zone.id)) {
                        <mat-icon class="!w-3.5 !h-3.5 !text-[14px] leading-3.5">check</mat-icon>
                        }
                        {{ zone.name || 'Unnamed Zone' }}
                    </button>
                    }
                </div>
            </div>
            }

            <!-- Section 6: Administrative Details & Organizer Info (Collapsible) -->
            <div class="p-4 sm:p-5 rounded-2xl bg-white border border-slate-200 shadow-2xs space-y-4">
                <button type="button" (click)="toggleAdminSection()"
                    class="w-full flex items-center justify-between text-left focus:outline-none">
                    <div class="flex items-center gap-2">
                        <mat-icon class="!w-4 !h-4 !text-[18px] leading-4 text-slate-500">assignment</mat-icon>
                        <h3 class="text-xs font-bold uppercase tracking-wider text-slate-600">
                            Organizer & Administrative Info
                        </h3>
                        <span class="px-2 py-0.5 rounded text-[10px] font-semibold bg-slate-100 text-slate-600">Optional</span>
                    </div>
                    <mat-icon class="!w-5 !h-5 !text-[20px] leading-5 text-slate-400 transition-transform"
                        [class.rotate-180]="showAdminSection()">
                        expand_more
                    </mat-icon>
                </button>

                @if (showAdminSection()) {
                <div class="space-y-4 pt-2 border-t border-slate-100 animate-fadeIn">
                    <div>
                        <label class="block text-xs font-semibold text-slate-700 mb-1.5">Cost Center</label>
                        <jjames-autocomplete-create
                            [options]="costCenterOptions()"
                            [value]="form.get('costCenterId')?.value"
                            [loading]="costCenterLoading()"
                            [disabled]="!form.get('organizationId')?.value"
                            placeholder="Search or type a new cost center…"
                            (valueChange)="onCostCenterSelected($event)"
                            (createNew)="onCostCenterCreateNew($event)">
                        </jjames-autocomplete-create>
                    </div>

                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                            <label class="block text-xs font-semibold text-slate-700 mb-1.5">Organizer Email</label>
                            <input type="email" formControlName="email" placeholder="organizer@domain.com"
                                class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                        </div>

                        <div>
                            <label class="block text-xs font-semibold text-slate-700 mb-1.5">Organizer Phone</label>
                            <input type="tel" formControlName="phone" placeholder="+43 660 1234567"
                                class="w-full px-3.5 py-2.5 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all" />
                        </div>
                    </div>

                    <div>
                        <label class="block text-xs font-semibold text-slate-700 mb-1.5">Internal Comment / Note</label>
                        <textarea formControlName="comment" rows="2"
                            placeholder="Internal operator memo (not visible to visitors or guests)"
                            class="w-full px-3.5 py-2 text-sm bg-white rounded-xl border border-slate-300 text-slate-900 placeholder:text-slate-400 shadow-2xs focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"></textarea>
                    </div>
                </div>
                }
            </div>

            }

        </form>
        }
    </div>

    <!-- Dialog Actions Footer -->
    <div class="flex items-center justify-between gap-3 p-4 sm:p-5 border-t border-slate-200 bg-slate-50/80 rounded-b-2xl shrink-0">
        @if (issued()) {
        <button type="button" (click)="resetForNew()"
            class="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs sm:text-sm font-semibold text-slate-700 hover:text-slate-900 rounded-xl hover:bg-slate-200/60 transition-colors">
            <mat-icon class="!w-4 !h-4 !text-[18px] leading-4 text-slate-500">add</mat-icon>
            <span>{{ 'easypass.createAnother' | translate }}</span>
        </button>

        <button type="button" (click)="close()"
            class="inline-flex items-center gap-2 px-5 py-2.5 text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-xl shadow-xs transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1">
            <mat-icon class="!w-4 !h-4 !text-[18px] leading-4">done_all</mat-icon>
            <span>Done</span>
        </button>
        } @else {
        <button type="button" (click)="close()"
            class="px-4 py-2.5 text-xs sm:text-sm font-semibold text-slate-600 hover:text-slate-800 rounded-xl hover:bg-slate-200/50 transition-colors">
            Cancel
        </button>

        <button type="button" (click)="submit()" [disabled]="isLoading() || costCenterLoading()"
            class="inline-flex items-center gap-2 px-5 py-2.5 text-xs sm:text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 disabled:cursor-not-allowed rounded-xl shadow-xs transition-all focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1">
            @if (isLoading()) {
            <mat-spinner diameter="18" class="!inline-block mr-1"></mat-spinner>
            <span>{{ 'easypass.creating' | translate }}</span>
            } @else {
            <mat-icon class="!w-4 !h-4 !text-[18px] leading-4">{{ mode() === 'simple' ? 'check_circle' : mode() === 'exitTickets' ? 'confirmation_number' : 'qr_code_2' }}</mat-icon>
            <span>{{ 'easypass.create' | translate }}</span>
            }
        </button>
        }
    </div>
</div>

